import { Immutable } from "immer";
import React from "react";
import { StarRating } from "tsi-common-react/src/components/StarRating/index";

import type { BrandMessageBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";

type Props = Immutable<{
    value: Value;
}>;

/**
 * React port of the pillows-specific ``BrandMessageBlock`` (defined in
 * ``server/cms/components/brand_message.py``). DOM mirrors the legacy
 * Django template ``cms/components/brand_message.html`` exactly —
 * StarRating + headline + RichText copy + CTA link.
 *
 * The legacy template applied ``{% round_to_half %}`` to the rating
 * before passing it to ``star-rating.html``; that step is now redundant
 * because ``StarRating``'s internal bucket thresholds
 * (``rating >= i + 0.75``, ``rating >= i + 0.25``) produce the same
 * visual output when passed the raw decimal value.
 *
 * The ``brand_message__start-rating`` class name carries a typo from
 * the legacy template (``start`` instead of ``star``) — preserved here
 * because downstream CSS may key off it.
 */
export const BrandMessageBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const rating = v.stars ? Number(v.stars) : 0;
    const buttonHref = v.button_link?.url ?? "";
    return (
        <div className="l-full-width brand_message">
            <div className="l-capped-width brand_message__inner">
                <StarRating
                    rating={rating}
                    className="brand_message__start-rating"
                />
                <h2 className="brand_message__headline">{v.headline ?? ""}</h2>
                <div className="brand_message__copy">
                    <RichTextBlock value={v.copy ?? ""} />
                </div>
                <a className="brand_message__link" href={buttonHref}>
                    <div className="button button-lochmara-pillows brand_message__button">
                        {v.button_name ?? ""}
                    </div>
                </a>
            </div>
        </div>
    );
};
